import React from 'react';
import { motion } from 'framer-motion';
import { Target, Users, Award, ShieldCheck, Brain, Leaf } from 'lucide-react';
import { Link } from 'react-router-dom';
import BrandName from '../components/BrandName';

const About = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6" style={{ background: 'linear-gradient(180deg, #A9C9D9 0%, #FFFFFF 100%)' }}>
        <div className="max-w-7xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl md:text-7xl font-bold mb-8" style={{ color: '#1A1A1A' }}>
              Building the Future of ESG
            </h1>
            <p className="text-xl md:text-2xl text-gray-700 max-w-4xl mx-auto">
              We're on a mission to make every sustainability claim verifiable, every carbon credit transparent, and every net-zero commitment achievable.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-32 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            className="grid md:grid-cols-2 gap-16 items-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div>
              <h2 className="text-4xl md:text-5xl font-bold mb-8" style={{ color: '#1A1A1A' }}>
                Our Story
              </h2>
              <div className="space-y-6 text-lg text-gray-700 leading-relaxed">
                <p>
                  <BrandName /> was founded on a simple observation: the world needs verifiable, transparent sustainability data more than ever.
                </p>
                <p>
                  Traditional ESG reporting is plagued by inconsistencies, greenwashing, and lack of real-time visibility. We knew there had to be a better way.
                </p>
                <p>
                  By combining the immutability of blockchain with the intelligence of AI, we're building a platform that doesn't just report on sustainability—it transforms how organizations understand, manage, and prove their environmental impact.
                </p>
                <p>
                  As an early-stage climate-tech startup, we're partnering with forward-thinking organizations ready to lead on transparency and reach net-zero with confidence.
                </p>
              </div>
            </div>
            <div className="rounded-3xl overflow-hidden shadow-2xl">
              <div className="bg-gradient-to-br from-blue-100 to-yellow-100 p-10 md:p-12">
                <div className="inline-flex p-4 rounded-2xl mb-6 bg-white/60">
                  <Target size={44} style={{ color: '#7CB8D6' }} />
                </div>
                <h3 className="text-2xl md:text-3xl font-bold mb-3" style={{ color: '#1A1A1A' }}>
                  Mission-Driven Innovation
                </h3>
                <p className="text-gray-700 leading-relaxed mb-8">
                  We build technology with a single purpose: to make sustainability data
                  trustworthy, actionable, and accessible for every organization on the path to net-zero.
                </p>
                <div className="space-y-4">
                  <MissionPillar icon={<ShieldCheck size={20} style={{ color: '#5a94b3' }} />} title="Verifiable by design" text="Every metric anchored to immutable, auditable records." />
                  <MissionPillar icon={<Brain size={20} style={{ color: '#5a94b3' }} />} title="AI at the core" text="Turning raw ESG data into predictive intelligence." />
                  <MissionPillar icon={<Leaf size={20} style={{ color: '#5a94b3' }} />} title="Built for net-zero" text="Tools that drive real, measurable climate impact." />
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-32 px-6" style={{ background: 'linear-gradient(180deg, #FFFFFF 0%, #F9FAFB 100%)' }}>
        <div className="max-w-7xl mx-auto">
          <motion.h2
            className="text-4xl md:text-5xl font-bold text-center mb-20"
            style={{ color: '#1A1A1A' }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            Our Values
          </motion.h2>

          <div className="grid md:grid-cols-3 gap-12">
            <ValueCard
              icon={<Target size={40} style={{ color: '#7CB8D6' }} />}
              title="Transparency Always"
              description="Every data point, every algorithm, every claim must be verifiable. No black boxes, no hidden calculations."
            />
            <ValueCard
              icon={<Users size={40} style={{ color: '#7CB8D6' }} />}
              title="Impact Over Profit"
              description="We measure success by the tons of CO2 reduced, not just revenue. Climate impact comes first."
            />
            <ValueCard
              icon={<Award size={40} style={{ color: '#7CB8D6' }} />}
              title="Innovation with Purpose"
              description="We use cutting-edge technology not because it's cool, but because it solves real sustainability challenges."
            />
          </div>
        </div>
      </section>

      {/* Capability Highlights Section */}
      <section className="py-32 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-12">
            <StatCard number="Scope 1–3" label="Emissions Coverage" />
            <StatCard number="Blockchain" label="Verified Records" />
            <StatCard number="AI-Driven" label="Predictive Insights" />
            <StatCard number="Audit-Ready" label="Compliance Reporting" />
          </div>
        </div>
      </section>

      {/* Team Teaser */}
      <section className="py-32 px-6" style={{ background: 'linear-gradient(180deg, #FFFFFF 0%, #A9C9D9 50%)' }}>
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-8" style={{ color: '#1A1A1A' }}>
              Meet Our Team
            </h2>
            <p className="text-xl text-gray-700 mb-12">
              Climate scientists, blockchain engineers, and sustainability experts united by a common mission
            </p>
            <Link to="/team">
              <motion.button
                className="btn-premium px-10 py-5 rounded-full text-lg font-semibold text-white"
                style={{ background: '#7CB8D6' }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                View Team
              </motion.button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

const MissionPillar = ({ icon, title, text }) => {
  return (
    <div className="flex items-start gap-3">
      <div className="p-2 rounded-xl bg-white/60 flex-shrink-0">{icon}</div>
      <div>
        <div className="font-semibold" style={{ color: '#1A1A1A' }}>{title}</div>
        <div className="text-sm text-gray-600">{text}</div>
      </div>
    </div>
  );
};

const ValueCard = ({ icon, title, description }) => {
  return (
    <motion.div
      className="card-premium p-10 rounded-3xl bg-white border-2 border-gray-100 text-center"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
    >
      <div className="inline-flex p-4 rounded-2xl mb-6" style={{ background: 'rgba(124, 184, 214, 0.1)' }}>
        {icon}
      </div>
      <h3 className="text-2xl font-bold mb-4" style={{ color: '#1A1A1A' }}>
        {title}
      </h3>
      <p className="text-gray-600 leading-relaxed">
        {description}
      </p>
    </motion.div>
  );
};

const StatCard = ({ number, label }) => {
  return (
    <motion.div
      className="text-center"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
    >
      <div className="text-3xl md:text-4xl font-bold mb-4" style={{ color: '#7CB8D6' }}>
        {number}
      </div>
      <div className="text-lg text-gray-600">{label}</div>
    </motion.div>
  );
};

export default About;